import { useEffect } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_CONFERENCE } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputNumber from "@/design/forms/InputNumber";
import InputNumeric from "@/design/forms/InputNumeric";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viPeopleCircle } from "@sippy-platform/valkyrie";

import { Container, Grid, IconButton, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import conferenceReducer from "./conferenceReducer";

function Conference() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const musicOnHoldStore = useSelector((state) => state.domain.musicOnHolds);

  // Get general data
  useEffect(() => {
    Promise.resolve(Api.getMusicOnHolds());
  }, []);

  const {
    value: conference,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_CONFERENCE,
    reducer: conferenceReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 40], { message: "Name must be at most 40 characters." }),
      exten: number()
        .required({ message: "Number is required." })
        .outside([100, 119], {
          message: "Number cannot be between 100 and 119.",
        })
        .not(0, { message: "Number cannot be 0." })
        .not(1711, { message: "Number cannot be 1711." })
        .not(1712, { message: "Number cannot be 1712." })
        .not(1722, { message: "Number cannot be 1722." })
        .not(1733, { message: "Number cannot be 1733." })
        .not(1813, { message: "Number cannot be 1813." }),
      maxMembers: number().min(2).max(15),
      pinAdmin: number()
        .required({
          condition: conference.enableAdmin,
          message: "You must set a PIN for the admin.",
        })
        .length([4, 8], {
          message: "The PIN must have between 4 and 8 characters.",
        })
        .truthy(conference.pinAdmin !== conference.pinParticipant, {
          message: "The PIN for admins cannot be the same as the PIN for participants.",
        }),
      pinParticipant: number()
        .required({
          condition: conference.enableAdmin,
          message: "You must set a PIN for the admin.",
        })
        .length([4, 8], {
          message: "The PIN must have between 4 and 8 characters.",
        })
        .truthy(conference.pinAdmin !== conference.pinParticipant, {
          message: "The PIN for participants cannot be the same as the PIN for admins.",
        }),
      announceMemberCountFrom: number()
        .min(1, { message: "The number of users must be between 1 and 15." })
        .max(15, { message: "The number of users must be between 1 and 15." }),
    }),
    onSubmit: () => Api.storeConference(conference),
    onSuccess: (response) => {
      if (!conference.id) {
        dispatch({ type: "id", value: response.data.conferenceRoom.id });
        navigate(`../${response.data.conferenceRoom.id}`);
      }

      Promise.resolve(Api.getConferences());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === conference.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_CONFERENCE });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getConference({ id: Number(id) }))
      .then((response) => !ignore && dispatch({ type: "set", value: response.data.conferenceRoom }))
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [conference.id, dispatch, id]);

  // Handle changes
  const formHandler = (property, value) => {
    dispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getConferences()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${conference.name || "Conference"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={conference.name} icon={viPeopleCircle} />
              </IconButton>
            ) : (
              <Avatar text={conference.name} icon={viPeopleCircle} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={conference.id || "nk"}
                isActionable={!!conference?.id}
                onDeleteCheck={() => Api.deleteConference(conference, false)}
                onDelete={() => Api.deleteConference(conference, true)}
                name={`${conference.name} (${conference.exten})`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={conference.name || "Unnamed conference"}
          subheader={conference.exten || "No number set"}
        />
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="name" label="Name" onChange={formHandler} value={conference.name} error={errors} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumber
              required
              id="exten"
              label="Number"
              onChange={formHandler}
              value={conference.exten}
              error={errors}
              helperText="Number cannot be 0 or any Belgian emergency number."
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Settings">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumeric
              required
              id="maxMembers"
              label="Max members"
              onChange={formHandler}
              value={conference.maxMembers}
              min={2}
              max={15}
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputSelect
              id="language"
              label="Language"
              error={errors}
              onChange={formHandler}
              value={conference.language}
              selects={[
                { value: "en_GB", name: "English (United Kingdom)" },
                { value: "en_US", name: "English (United States)" },
                { value: "fr_FR", name: "French" },
                { value: "nl_BE", name: "Dutch" },
              ]}
              getOptionLabel={(option) => option.name}
              helperText="The language of the announcements."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputAutocomplete
              hasEmpty={null}
              id="moh"
              label="Music on hold"
              onChange={formHandler}
              value={conference.moh ?? null}
              selectValue="name"
              selects={musicOnHoldStore.list}
              getOptionLabel={(option) => option.name}
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              id="record"
              error={errors}
              onChange={formHandler}
              states={[false, true]}
              value={conference.record}
              primary="Record this conference"
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Security">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              id="enableAdmin"
              error={errors}
              onChange={formHandler}
              states={[false, true]}
              value={conference.enableAdmin}
              primary="Enable admin"
              secondary="Enable the admin role within this conference."
            />
          </Grid>
          {conference.enableAdmin && (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  required
                  type="number"
                  id="pinAdmin"
                  label="Admin PIN code"
                  onChange={formHandler}
                  value={conference.pinAdmin}
                  error={errors}
                  helperText="The PIN code required to log in as admin."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="waitForAdmin"
                  error={errors}
                  onChange={formHandler}
                  states={[false, true]}
                  value={conference.waitForAdmin}
                  primary="Wait for admin"
                  secondary="Wait until the admin joins for the conference to start."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="quitOnAdminLeave"
                  error={errors}
                  onChange={formHandler}
                  states={[false, true]}
                  value={conference.quitOnAdminLeave}
                  primary="End conference by admin"
                  secondary="End the conference call as soon as the admin leaves."
                />
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText
              required={conference.enableAdmin}
              type="number"
              id="pinParticipant"
              label="Participant PIN code"
              onChange={formHandler}
              value={conference.pinParticipant}
              error={errors}
              helperText="The PIN code required to log in as a participant."
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Announcements">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              id="useNameForPresence"
              error={errors}
              onChange={formHandler}
              states={[false, true]}
              value={conference.useNameForPresence}
              primary="Use name to announce presence"
              secondary="When enabled participants will be asked to say their name when joining."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              id="announceUserCount"
              error={errors}
              onChange={formHandler}
              states={[false, true]}
              value={conference.announceUserCount}
              primary="Announce member count"
              secondary="Announce how many members are in the conference."
            />
          </Grid>
          {conference.announceUserCount && (
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <InputNumeric
                id="announceUserCountFrom"
                label="Announce member count from"
                onChange={formHandler}
                value={conference.announceUserCountFrom}
                min={1}
                max={15}
                error={errors}
                helperText="Number of minimum members before the member count is announced."
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              id="announceOnlyUser"
              error={errors}
              onChange={formHandler}
              states={[false, true]}
              value={conference.announceOnlyUser}
              primary="Announce only member"
              secondary="Announce when the members is the only member in the conference."
            />
          </Grid>
        </FormGroup>
      </Stack>
    </Container>
  );
}

export default Conference;
